import { Session } from "@prisma/client/index";
import { Link } from "react-router";
import CalendarItem from "~/components/shared/CalendarItem";
import { Button } from "~/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { getSessionsByVenueAndTime } from "~/lib/session";
import { useEffect, useState } from "react";

import NoSessionImage from "./no-shows.png";
import { venuePath } from "~/services/url-builder";

type SessionVenue = {
  id: string;
  city: string;
  name: string;
};

type SessionGroup = {
  venue: SessionVenue;
  sessions: Session[];
  ticket_url?: string;
};

type SessionListProps = {
  sessions: Session[];
};

export default function SessionList({ sessions }: SessionListProps) {
  const [isMounted, setIsMounted] = useState(false);
  const { past: pastSessions, future: futureSessions } = getSessionsByVenueAndTime(sessions) as {
    past: SessionGroup[];
    future: SessionGroup[];
  };

  const hasPastSessions = !!pastSessions.length;
  const hasfutureSessions = !!futureSessions.length;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null; // or a loading state/skeleton
  }

  return (
    <Tabs defaultValue="sessions">
      <TabsList className="w-full flex flex-row justify-evenly h-14 rounded-bl-none rounded-br-none">
        <TabsTrigger value="sessions" className="w-full font-medium py-3">
          Sessões
        </TabsTrigger>

        <TabsTrigger value="history" className="w-full font-medium text py-3">
          Histórico
        </TabsTrigger>
      </TabsList>

      <TabsContent value="sessions" className="bg-white">
        {!hasfutureSessions && (
          <div className="p-8 flex flex-col items-center border-b gap-8">
            <img src={NoSessionImage} width={150} height={150} className="opacity-70" />
            <p className="text-sm text-gray-400 font-medium text-center">
              Não encontramos espetáculos agendados para esta peça :(
            </p>
          </div>
        )}
        {!!hasfutureSessions &&
          futureSessions.map((sessionGroup) => <SessionGroup key={sessionGroup.venue.id} group={sessionGroup} />)}
      </TabsContent>

      <TabsContent value="history" className="bg-white">
        {!hasPastSessions && (
          <div className="p-8 flex flex-col items-center border-b gap-8">
            <img src={NoSessionImage} width={150} height={150} className="opacity-70" />
            <p className="text-sm text-gray-400 font-medium text-center">
              Não encontramos registos de espetáculos anteriores :(
            </p>
          </div>
        )}

        {!!hasPastSessions &&
          pastSessions.map((sessionGroup: SessionGroup) => (
            <SessionGroup key={sessionGroup.venue.id} group={sessionGroup} allowPurchase={false} outdated={true} />
          ))}
      </TabsContent>
    </Tabs>
  );
}

type SessionGroupProps = {
  group: SessionGroup;
  allowPurchase?: boolean;
  outdated?: boolean;
};

function SessionGroup({ group, allowPurchase = true, outdated = false }: SessionGroupProps) {
  return (
    <div className="p-5 flex flex-col items-end border-b">
      <span className="text-sm font-medium text-gray-600 w-full">{group.venue.city}</span>
      <Link to={venuePath(group.venue)} className="text-lg font-medium text-gray-800 mb-3 w-full underline">
        {group.venue.name}
      </Link>

      <div className="flex flex-row flex-wrap w-full min-h-28 gap-2 justify-stretch">
        {group.sessions.map((session: Session) => (
          <CalendarItem key={session.id} datetime={session.datetime} outdated={outdated} />
        ))}
      </div>

      {!!group.ticket_url && allowPurchase && (
        <Link to={group.ticket_url} target="_blank">
          <Button className="mt-4 bg-gray-900 hover:bg-orange-700 text-white text-sm">Comprar bilhetes</Button>
        </Link>
      )}
    </div>
  );
}
